"use client";
import { useResettableState } from "@/src/hooks/useResettableState";

import { useScrollReveal } from "@/src/hooks/useScrollReveal";
import { buildProductPath } from "@/src/seo/urlHelpers";
import { getUrlImage, PersianNum } from "@/src/tools";
import {
  getCanonicalWarehouse,
  getEffectiveWarehousePrice,
  hasPurchasableStock,
} from "@/src/tools/warehouseAvailability";
import {
  ProductIdentityProductListType,
  RowProductListType,
} from "@/src/types/product";
import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";
import { Star } from "lucide-react";
import { useMemo, useRef, useState } from "react";

const ITEM_SIZE = 48;
const ITEM_GAP = 4;


function pickAlbum(idn?: ProductIdentityProductListType) {
  const img = idn?.Albums?.[0];
  return {
    src: img?.album_image_src || "/logo.webp",
    alt: img?.album_image_alt || "تصویر محصول",
  };
}

function getBorderColor(i: ProductIdentityProductListType) {
  const raw = i.ProdProperties?.[0]?.Attribute?.attribute_label || "#223843";
  return String(raw).startsWith("#") ? String(raw) : `#${raw}`;
}

interface CardProductProps {
  data: RowProductListType;
  active?: number[];
  variant?: "default" | "home-new";
  imageSizes?: string;
  priority?: boolean;
}

const CardProduct: React.FC<CardProductProps> = ({
  data,
  active,
  variant = "default",
  imageSizes,
  priority = false,
}) => {
  const productPath = buildProductPath(data.product_url);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const revealRef = useScrollReveal() as React.RefObject<HTMLDivElement>;
  const sliderRef = useRef<HTMLDivElement>(null);
  const activeValues = useMemo(() => active ?? [], [active]);
  const activeSignature = activeValues.join(",");
  const hasActiveFilters = activeValues.length > 0;
  const [showAll, setShowAll] = useResettableState(hasActiveFilters, activeSignature);

  // برای اینکه افکتِ «انتخاب‌اولیه» بعد از کلیکِ کاربر دوباره state را خراب نکند

  // اسکرول افقی
  const [isDown, setIsDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScroll] = useState(0);

  // همیشه main را جلو بیاور
  const sortedIdentities = useMemo(() => {
    const arr = [...(data.ProductIdentities || [])];
    arr.sort(
      (a, b) =>
        (b.product_identity_main ? 1 : 0) - (a.product_identity_main ? 1 : 0),
    );
    return arr;
  }, [data.ProductIdentities]);
  const preferredIdentity = useMemo(
    () =>
      data.product_type === "simple"
        ? sortedIdentities[0]
        : sortedIdentities.find((identity) =>
            identity.Warehouses?.some((warehouse) =>
              hasPurchasableStock(warehouse),
            ),
          ) ?? sortedIdentities[0],
    [data.product_type, sortedIdentities],
  );

  const filteredIdentity = hasActiveFilters ? sortedIdentities.find((identity) =>
    identity.ProdProperties?.some((property) => activeValues.includes(property.Attribute?.id ?? -1)),
  ) : undefined;
  const currentSelectedId = selectedId ?? filteredIdentity?.id ?? preferredIdentity?.id ?? null;

  const currentIdentity = useMemo(
    () =>
      sortedIdentities.find((i) => i.id === currentSelectedId) ||
      preferredIdentity,
    [sortedIdentities, currentSelectedId, preferredIdentity],
  );

  // داده‌های تصویر و انبار
  const mainImg = pickAlbum(currentIdentity);
  // اگر product_type ساده باشد، از warehouse محصول استفاده کن، وگرنه از warehouse هویت
  const warehouse = getCanonicalWarehouse(
    data.product_type === "simple"
      ? data.Warehouses
      : currentIdentity?.Warehouses,
  );

  const totalVariants = sortedIdentities.length;
  const visibleCount = showAll ? totalVariants : Math.min(3, totalVariants);
  const identitiesToShow = sortedIdentities.slice(0, visibleCount);
  const remainingCount = totalVariants - visibleCount;

  const regularPrice = Number(warehouse?.warehouse_price) || 0;
  const effectivePrice = getEffectiveWarehousePrice(warehouse);
  const discountPercent =
    regularPrice > effectivePrice && effectivePrice > 0
      ? Math.round(((regularPrice - effectivePrice) / regularPrice) * 100)
      : 0;
  const discountFa = PersianNum(discountPercent);
  const maxWidthValue =
    visibleCount * ITEM_SIZE + (visibleCount - 1) * ITEM_GAP;

  // Drag handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!showAll || !sliderRef.current) return;
    setIsDown(true);
    setStartX(e.pageX - sliderRef.current.offsetLeft);
    setScroll(sliderRef.current.scrollLeft);
  };
  const stopDrag = () => setIsDown(false);
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDown || !sliderRef.current) return;
    e.preventDefault();
    const x = e.pageX - sliderRef.current.offsetLeft;
    sliderRef.current.scrollLeft = scrollLeft - (x - startX);
  };

  // وضعیت و قیمت همیشه متعلق به همان هویت انتخاب‌شده هستند.
  const isAvailable = hasPurchasableStock(warehouse);
  const hasDiscount =
    isAvailable &&
    effectivePrice > 0 &&
    effectivePrice < regularPrice;

  if (variant === "home-new") {
    return (
      <article
        ref={revealRef}
        className="group relative mx-auto flex h-full min-h-[252px] w-full flex-col overflow-hidden rounded-xl bg-white p-2 shadow-[0_10px_24px_-18px_rgba(15,23,42,0.5)] ring-1 ring-black/5 transition duration-300 hover:-translate-y-1 hover:shadow-[0_18px_32px_-20px_rgba(15,23,42,0.6)]"
      >
        {data.product_is_featured ? (
          <div className="absolute right-0 top-0 z-20 inline-flex h-7 items-center gap-1 rounded-bl-lg bg-[#ff8a00] px-2 text-[9px] font-bold text-white shadow-sm sm:text-[10px]">
            <Star className="size-3 fill-current" />
            پیشنهاد ویژه
          </div>
        ) : null}

        <Link
          href={productPath}
          className="flex h-full flex-1 flex-col"
        >
          <div className="relative aspect-square w-full overflow-hidden rounded-lg bg-[#f5f5f5]">
            <Image
              width={220}
              height={220}
              sizes={imageSizes ?? "(max-width: 639px) 42vw, (max-width: 1023px) 205px, 220px"}
              priority={priority}
              alt={mainImg.alt || data.product_image_alt || data.product_title}
              src={getUrlImage(data.product_image || mainImg.src || "/logo.webp")}
              className="h-full w-full object-contain p-3 transition-transform duration-500 group-hover:scale-105"
            />

            <span className="pointer-events-none absolute left-3 top-3 h-px w-10 bg-[#4268ff]" />
            <span className="pointer-events-none absolute right-3 top-3 h-10 w-px bg-[#ff8a4b]" />
            <span className="pointer-events-none absolute bottom-3 left-3 h-9 w-px bg-[#4268ff]" />
            <span className="pointer-events-none absolute bottom-3 left-3 h-px w-12 bg-[#ff8a4b]" />
            <span className="pointer-events-none absolute bottom-2.5 left-2 text-[10px] font-black text-[#4268ff]">
              +
            </span>
          </div>

          <h3
            className="mt-2 min-h-10 px-1 text-center text-[10px] font-medium leading-5 text-slate-800 line-clamp-2 sm:text-[11px]"
            title={data.product_title}
          >
            {data.product_title}
          </h3>

          <div className="mt-auto min-h-11 px-1 pb-1 pt-1">
            {data.product_coming_soon ? (
              <p className="rounded-md bg-blue-50 py-1.5 text-center text-[11px] font-black text-[#1455C6]">
                به‌زودی
              </p>
            ) : !isAvailable ? (
              <p className="rounded-md bg-rose-50 py-1.5 text-center text-[11px] font-bold text-rose-500">
                ناموجود
              </p>
            ) : hasDiscount ? (
              <div className="flex items-end justify-between gap-1">
                <span className="shrink-0 rounded bg-[#1455c6] px-1.5 py-1 text-[9px] font-black text-white">
                  ٪{discountFa}-
                </span>
                <div className="min-w-0 text-left">
                  <p className="truncate text-[9px] text-slate-400 line-through">
                    {PersianNum(regularPrice)}
                  </p>
                  <p className="whitespace-nowrap text-[11px] font-black text-slate-900 sm:text-xs">
                    {PersianNum(effectivePrice)}
                    <span className="mr-1 text-[8px] font-medium text-slate-500">
                      تومان
                    </span>
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-left text-[11px] font-black text-slate-900 sm:text-xs">
                {PersianNum(effectivePrice)}
                <span className="mr-1 text-[8px] font-medium text-slate-500">
                  تومان
                </span>
              </p>
            )}
          </div>
        </Link>
      </article>
    );
  }

  return (
    <div
      ref={revealRef}
      className="group relative mx-auto w-full max-w-[230px] overflow-hidden rounded-[14px] bg-white p-2.5 shadow-[0_10px_28px_-22px_rgba(15,23,42,0.45)] ring-1 ring-slate-100 transition hover:-translate-y-0.5 hover:shadow-[0_18px_42px_-28px_rgba(15,23,42,0.55)]"
    >
      {data.product_is_featured ? (
        <div className="absolute right-0 top-0 z-20 inline-flex h-8 items-center gap-1 rounded-bl-md rounded-tr-[14px] bg-[#ff8a00] px-2.5 text-[10px] font-bold text-white shadow-sm">
          <Star className="size-3 fill-current" />
          پیشنهاد ویژه
        </div>
      ) : null}
      <span className="pointer-events-none absolute right-0 top-8 z-10 h-20 w-px bg-[#ff4b23]" />
      {/* تصویر و عنوان */}
      <Link
        href={productPath}
        className="block cursor-pointer"
      >
        <div className="relative mb-3 w-full overflow-hidden rounded-[10px] bg-[#f4f4f4]">
          <div className="relative aspect-square w-full overflow-hidden">
            <Image
              key={ mainImg.src} // تضمین ریمونت وقتی src عوض می‌شود
              width={336}
              height={336}
              sizes={imageSizes ?? "230px"}
              priority={priority}
              alt={mainImg.alt || data.product_image_alt || data.product_title}
              src={getUrlImage(data.product_image || mainImg.src || "/logo.webp")}
              className="h-full w-full object-contain p-3 transition-transform duration-500 group-hover:scale-105"
            />
          </div>
          <span className="absolute bottom-2 left-3 h-10 w-px bg-[#4169ff]" />
          <span className="absolute bottom-2 left-2.5 text-[11px] font-bold text-[#4169ff]">
            +
          </span>
          <span className="absolute bottom-3 left-4 h-px w-14 bg-[#ff8a4b]" />
        </div>
        <p className="mb-2 min-h-9 px-1 text-center text-[12px] font-medium leading-5 text-slate-800 transition-colors line-clamp-2">
          {data.product_title}
        </p>
      </Link>

      {/* نوار واریانت‌ها + دکمه +N (با z-index برای حل کلیک اول) */}
      <div className="relative mt-2 flex items-center justify-center">
        <div
          ref={sliderRef}
          onMouseDown={handleMouseDown}
          onMouseLeave={stopDrag}
          onMouseUp={stopDrag}
          onMouseMove={handleMouseMove}
          className={clsx(
            "variants-scroll swiper-no-swiping",
            "relative z-10 flex items-center gap-2 py-1 whitespace-nowrap",
            showAll
              ? "hide-scrollbar cursor-grab overflow-x-auto"
              : "overflow-hidden",
          )}
          style={{
            maxWidth: showAll ? "100%" : `${maxWidthValue}px`,
          }}
        >
          {identitiesToShow.map((idn) => {
            const color = getBorderColor(idn);
            const variant = pickAlbum(idn);
            const isSelected = idn.id === currentSelectedId;
            const variantAvailable =
              data.product_type === "simple"
                ? isAvailable
                : idn.Warehouses?.some((warehouse) =>
                    hasPurchasableStock(warehouse),
                  );

            return (
              <button
                type="button"
                key={idn.id}
                aria-label={`${variant.alt}${variantAvailable ? "" : "، ناموجود"}`}
                title={variantAvailable ? variant.alt : `${variant.alt}، ناموجود`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedId(idn.id);
                }}
                className={clsx(
                  "flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border border-solid bg-card text-[10px] transition-all sm:h-12 sm:w-12",
                  isSelected
                    ? "border-4 shadow-inner"
                    : "border-2 text-slate-500",
                  !variantAvailable && "opacity-45 grayscale",
                )}
                style={{
                  borderColor: color,
                  boxShadow: isSelected ? `0 0 0 2px ${color}80` : "none",
                }}
              >
                <Image
                  width={44}
                  height={44}
                  alt={variant.alt}
                  src={getUrlImage(variant.src)}
                  className="h-10 w-10 rounded-full object-cover sm:h-12 sm:w-12"
                />
              </button>
            );
          })}
        </div>

        {/* دکمه +N */}
        {!hasActiveFilters && remainingCount > 0 && (
          <button
            onMouseDown={(e) => {
              // جلوگیری از درگ/اسکرول و بلع رویداد توسط اسلایدر
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowAll((p) => !p);
            }}
            className="relative z-20 mr-2 flex h-11 w-11 items-center justify-center rounded-full border border-slate-300 text-xs font-semibold text-slate-600 transition hover:border-slate-500 hover:bg-slate-50 hover:text-slate-700 dark:border-slate-500 dark:text-slate-200 dark:hover:border-slate-400 dark:hover:bg-slate-800 sm:h-12 sm:w-12 sm:text-sm"
          >
            <span className="text-sm font-bold">
              +{PersianNum(remainingCount)}
            </span>
          </button>
        )}
      </div>

      {data.product_coming_soon ? (
        <p className="mt-3 rounded-lg bg-blue-50 py-2 text-center text-xs font-black text-[#1455C6]">
          به‌زودی
        </p>
      ) : !isAvailable ? (
        <p className="mt-3 rounded-lg bg-slate-50 py-2 text-center text-xs font-semibold text-rose-500">
          ناموجود
        </p>
      ) : hasDiscount ? (
        <div className="mt-3 flex items-center justify-between gap-2 rounded-lg bg-[#f7f7f7] px-2 py-2">
          <div className="flex min-w-0 flex-col items-end">
            <p className="text-[13px] font-extrabold text-slate-900">
              {PersianNum(effectivePrice)} تومان
            </p>
            <p className="text-[10px] text-slate-400 line-through">
              {PersianNum(regularPrice)} تومان
            </p>
          </div>
          <div className="shrink-0 rounded-[5px] bg-[#e03131] px-2 py-1 text-[11px] font-extrabold text-white">
            -{discountFa}%
          </div>
        </div>
      ) : (
        <div className="mt-3 rounded-lg bg-[#f7f7f7] px-2 py-2 text-left">
          <p className="text-[13px] font-extrabold text-slate-900">
            {PersianNum(effectivePrice)} تومان
          </p>
        </div>
      )}
    </div>
  );
};

export default CardProduct;
